import React from "react";
import { Box, Grid } from "@mui/material";
import { SearchSettings, TourSearchResult } from "@/types/search/tour-search-results";
import TourCard from "@/components/common/tour-card/tour-card";
import TourSearchResultsSkeleton from "@/components/find-your-tour-page/tour-search-results.skeleton";
import NoSearchResults from "@/components/find-your-tour-page/no-search-results";
import { useTranslations } from "next-intl";
import TourSearchResultsPagination from "@/components/find-your-tour-page/tour-search-results-pagination";
import { getStartAndEndIndex } from "@/utils/search-utils";

interface SearchResultsProps {
  tours: TourSearchResult[];
  isLoading: boolean;
  isError: boolean;
  searchSettings?: SearchSettings;
  handlePageChange: (event: React.ChangeEvent<unknown>, value: number) => void;
  errorOnClick: () => void;
  clearFilters: () => void;
  spacing?: number;
  openRequestAQuote: (tour: TourSearchResult) => void;
  onFilterInteraction?: (filterType: string, filterValue: string | string[], action: "page") => void;
}

const TourSearchResults = ({
  tours,
  isLoading,
  isError,
  searchSettings,
  handlePageChange,
  errorOnClick,
  clearFilters,
  spacing = 4,
  openRequestAQuote,
  onFilterInteraction
}: SearchResultsProps) => {
  const t = useTranslations("SEARCH");

  const paginationLabelIndices = getStartAndEndIndex(
    searchSettings?.pageSize ?? 0,
    searchSettings?.currentPage ?? 0,
    searchSettings?.totalCount ?? 0
  );

  if (isLoading) {
    return (<TourSearchResultsSkeleton />);
  }

  if (isError) {
    return (
      <Box
        sx={{
          width: "100%",
          padding: "8.5rem",
          justifyContent: "center"
        }}
      >
        <NoSearchResults header={t("error")} buttonLabel={t("Retry")} onClick={errorOnClick} />
      </Box>
    );
  }

  if (searchSettings?.totalCount === 0 || tours.length === 0) {
    return (
      <Box
        sx={{
          width: "100%",
          padding: "8.5rem",
          justifyContent: "center"
        }}
      >
        <NoSearchResults header={t("noResults")} buttonLabel={t("Clear All Filters")} onClick={clearFilters} />
      </Box>
    );
  }

  return (
    <>
      <Grid
        container
        columnSpacing={spacing}
        wrap="wrap"
        justifyContent="flex-start"
        width="100%"
        sx={{
          containerType: "inline-size",
          containerName: "tour-search-results-grid"
        }}
        data-testid="tour-search-results-grid"
      >
        {tours.map((tour) => (
          <Grid
            key={tour.packageDateId}
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "flex-start",
              minWidth: "30rem",
              marginBottom: `${spacing * 8}px`
            }}
            size="grow"
          >
            <TourCard {...tour} openRequestAQuote={() => openRequestAQuote(tour)} />
          </Grid>
        ))}
        {/* eslint-disable-next-line @typescript-eslint/no-unused-vars */}
        {Array.from({ length: (searchSettings?.pageSize ?? 12) - tours.length }).map((_, index) => (
          <Grid
            // eslint-disable-next-line react/no-array-index-key
            key={`ghost-${index}`}
            sx={{
              display: "flex",
              alignItems: "stretch",
              minWidth: "30rem",
              visibility: "hidden"
            }}
            size="grow"
            data-testid="ghost-card"
          />
        ))

        }
      </Grid>
      <Box
        sx={{
          display: "flex",
          width: "100%",
          justifyContent: "center"
        }}
      >
        <TourSearchResultsPagination
          page={searchSettings?.currentPage ?? 1}
          handlePageChange={handlePageChange}
          pageCount={searchSettings?.pageCount ?? 1}
          totalCount={searchSettings?.totalCount ?? 0}
          startIndex={paginationLabelIndices.start}
          endIndex={paginationLabelIndices.end}
          onFilterInteraction={onFilterInteraction}
        />
      </Box>
    </>
  );
};

export default TourSearchResults;